import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosInstance from "../config/Axios";
import { handleResErrors } from "./utils";
import { useCustomToast } from "../pages/Contexts/ToastContext";

// ─── Agencies ────────────────────────────────────────────────────────────────

export const useGetAgencies = (params = {}) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["agencies", params],
        () => axiosInstance.get("/agencies", { params }).then((r) => r.data),
        { keepPreviousData: true, onError: (e) => handleResErrors(e, showToast) }
    );
};

export const useCreateAgency = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (payload) => axiosInstance.post("/agencies/create", payload).then((r) => r.data),
        {
            onSuccess: () => { qc.invalidateQueries("agencies"); showToast("success", "Agencies", "Agency created!"); },
            onError: (e) => handleResErrors(e, showToast),
        }
    );
};

export const useUpdateAgency = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        ({ id, ...payload }) => axiosInstance.put(`/agencies/${id}/update`, payload).then((r) => r.data),
        {
            onSuccess: () => { qc.invalidateQueries("agencies"); showToast("success", "Agencies", "Agency updated!"); },
            onError: (e) => handleResErrors(e, showToast),
        }
    );
};

export const useDeleteAgency = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (id) => axiosInstance.delete(`/agencies/${id}/delete`).then((r) => r.data),
        {
            onSuccess: () => { qc.invalidateQueries("agencies"); showToast("success", "Agencies", "Agency removed!"); },
            onError: (e) => handleResErrors(e, showToast),
        }
    );
};

// ─── Ad Campaigns ─────────────────────────────────────────────────────────────

export const useGetAdCampaigns = (params = {}) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["adCampaigns", params],
        () => axiosInstance.get("/ad-campaigns", { params }).then((r) => r.data),
        { keepPreviousData: true, onError: (e) => handleResErrors(e, showToast) }
    );
};

export const useGetAdCampaign = (id) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["adCampaign", id],
        () => axiosInstance.get(`/ad-campaigns/${id}`).then((r) => r.data),
        { enabled: !!id, onError: (e) => handleResErrors(e, showToast) }
    );
};

export const useCreateAdCampaign = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (payload) => axiosInstance.post("/ad-campaigns/create", payload).then((r) => r.data),
        {
            onSuccess: () => { qc.invalidateQueries("adCampaigns"); showToast("success", "Ad Campaigns", "Campaign created!"); },
            onError: (e) => handleResErrors(e, showToast),
        }
    );
};

export const useUpdateAdCampaign = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        ({ id, ...payload }) => axiosInstance.put(`/ad-campaigns/${id}/update`, payload).then((r) => r.data),
        {
            onSuccess: (_, vars) => {
                qc.invalidateQueries("adCampaigns");
                qc.invalidateQueries(["adCampaign", vars.id]);
                showToast("success", "Ad Campaigns", "Campaign updated!");
            },
            onError: (e) => handleResErrors(e, showToast),
        }
    );
};

export const useDeleteAdCampaign = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (id) => axiosInstance.delete(`/ad-campaigns/${id}/delete`).then((r) => r.data),
        {
            onSuccess: () => { qc.invalidateQueries("adCampaigns"); showToast("success", "Ad Campaigns", "Campaign deleted!"); },
            onError: (e) => handleResErrors(e, showToast),
        }
    );
};

// Status transitions
const makeStatusMutation = (action, successMsg) => () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        ({ id, ...body }) => axiosInstance.post(`/ad-campaigns/${id}/${action}`, body).then((r) => r.data),
        {
            onSuccess: () => { qc.invalidateQueries("adCampaigns"); showToast("success", "Ad Campaigns", successMsg); },
            onError: (e) => handleResErrors(e, showToast),
        }
    );
};

export const useSubmitCampaignForReview = makeStatusMutation("submit", "Submitted for review!");
export const useApproveCampaign         = makeStatusMutation("approve", "Campaign approved!");
export const useRejectCampaign          = makeStatusMutation("reject", "Campaign rejected.");
export const usePauseCampaign           = makeStatusMutation("pause", "Campaign paused.");
export const useResumeCampaign          = makeStatusMutation("resume", "Campaign resumed!");

// Campaign stats
export const useAdCampaignStats = (id) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["adCampaignStats", id],
        () => axiosInstance.get(`/ad-campaigns/${id}/stats`).then((r) => r.data),
        { enabled: !!id, staleTime: 60_000, onError: (e) => handleResErrors(e, showToast) }
    );
};

export const useAdCampaignImpressions = (id, page = 1) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["adImpressions", id, page],
        () => axiosInstance.get(`/ad-campaigns/${id}/impressions`, { params: { page, per_page: 25 } }).then((r) => r.data),
        { enabled: !!id, keepPreviousData: true, onError: (e) => handleResErrors(e, showToast) }
    );
};

// ─── Venue owner: points ──────────────────────────────────────────────────────

export const useMyPoints = () => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["myPoints"],
        () => axiosInstance.get("/ad-marketplace/my/points").then((r) => r.data),
        { onError: (e) => handleResErrors(e, showToast) }
    );
};

export const useMyPointsLedger = (page = 1) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["myPointsLedger", page],
        () => axiosInstance.get("/ad-marketplace/my/points/ledger", { params: { page, per_page: 20 } }).then((r) => r.data),
        { keepPreviousData: true, onError: (e) => handleResErrors(e, showToast) }
    );
};

export const useRedeemPoints = () => {
    const qc = useQueryClient();
    const { showToast } = useCustomToast();
    return useMutation(
        (payload) => axiosInstance.post("/ad-marketplace/my/points/redeem", payload).then((r) => r.data),
        {
            onSuccess: (data) => {
                qc.invalidateQueries("myPoints");
                qc.invalidateQueries("myPointsLedger");
                showToast("success", "Points", data?.msg ?? "Points redeemed!");
            },
            onError: (e) => handleResErrors(e, showToast),
        }
    );
};

export const useMyImpressions = (page = 1) => {
    const { showToast } = useCustomToast();
    return useQuery(
        ["myImpressions", page],
        () => axiosInstance.get("/ad-marketplace/my/impressions", { params: { page, per_page: 25 } }).then((r) => r.data),
        { keepPreviousData: true, onError: (e) => handleResErrors(e, showToast) }
    );
};
